import type {
  AppImportApi,
  ImportCapability,
  ImportFormat,
  ImportResult,
} from '@yada/plugin-api';

interface ImportEntry {
  appName: string;
  capabilities: ImportCapability[];
  handler: (
    data: string | Blob,
    format: ImportFormat,
    capabilityId?: string
  ) => Promise<ImportResult>;
}

export class ImportRegistry {
  private entries = new Map<string, ImportEntry>();

  register(
    appId: string,
    appName: string,
    capabilities: ImportCapability[],
    handler: (
      data: string | Blob,
      format: ImportFormat,
      capabilityId?: string
    ) => Promise<ImportResult>
  ): void {
    this.entries.set(appId, { appName, capabilities, handler });
  }

  unregister(appId: string): void {
    this.entries.delete(appId);
  }

  list(): Array<{ appId: string; appName: string; capabilities: ImportCapability[] }> {
    return Array.from(this.entries.entries()).map(([appId, entry]) => ({
      appId,
      appName: entry.appName,
      capabilities: entry.capabilities,
    }));
  }

  async import(
    appId: string,
    data: string | Blob,
    format: ImportFormat,
    capabilityId?: string
  ): Promise<ImportResult> {
    const entry = this.entries.get(appId);
    if (!entry) {
      return {
        success: false,
        itemsImported: 0,
        errors: [`No import handler registered for app: ${appId}`],
      };
    }

    const cap = capabilityId
      ? entry.capabilities.find((c) => c.id === capabilityId)
      : entry.capabilities.find((c) => c.format === format);

    if (!cap) {
      return {
        success: false,
        itemsImported: 0,
        errors: [
          capabilityId
            ? `Import capability "${capabilityId}" not found in ${appId}`
            : `Format "${format}" not supported by ${appId}`,
        ],
      };
    }

    try {
      return await entry.handler(data, cap.format, cap.id);
    } catch (err) {
      return {
        success: false,
        itemsImported: 0,
        errors: [err instanceof Error ? err.message : String(err)],
      };
    }
  }

  createApi(appId: string, appName: string): AppImportApi {
    return {
      register: (capabilities, handler) =>
        this.register(appId, appName, capabilities, handler),
    };
  }
}
